import { createRouter, createWebHistory } from 'vue-router'
import type { Pinia } from 'pinia'
import { useAuthStore } from './stores/auth'
import { initDebugFromRoute } from './composables/useDebugMode'
import LoginView from './views/LoginView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      name: 'home-dashboard',
      component: () => import('./views/HomeDashboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/records',
      name: 'records',
      component: () => import('./views/RecordsView.vue'),
      meta: { requiresAuth: true },
    },
  ],
})

/**
 * 设置路由守卫
 *
 * 必须在 Pinia 安装到应用后调用此函数
 *
 * @param pinia - Pinia 实例
 */
export function setupRouterGuards(pinia: Pinia): void {
  // 获取 auth store（此时 Pinia 已经安装）
  const authStore = useAuthStore(pinia)

  // 初始化认证状态（从 localStorage 恢复）
  authStore.initialize()

  // 路由守卫：检查认证状态
  router.beforeEach(async (to, _from, next) => {
    // 检查 token 是否有效（包括过期检查）
    const isValidAuth = authStore.isAuthenticated && authStore.checkTokenValidity()

    // 检查路由是否需要认证
    if (to.meta.requiresAuth && !isValidAuth) {
      // 未认证或 token 过期，重定向到登录页
      if (authStore.isAuthenticated) {
        // token 过期，先登出清除状态
        authStore.logout()
      }
      next({ name: 'login', query: { redirect: to.fullPath } })
    } else if (to.name === 'login' && isValidAuth) {
      // 已认证但访问登录页，重定向到首页
      next({ name: 'home-dashboard' })
    } else {
      // 组件渲染前初始化 debug 模式（解决 child onMounted 先于 parent 的问题）
      if (to.query.debug === 'true' && to.name === 'home-dashboard') {
        await initDebugFromRoute(
          to.query as Record<string, string>,
          (path) => next({ path, replace: true })
        )
        // debug 初始化后继续（去掉 debug 参数避免重复触发）
        const { debug, ...rest } = to.query
        next({ ...to, query: rest, replace: true })
        return
      }
      next()
    }
  })
}

export default router
